import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

// Small Status Badge component
const StatusBadge = ({ isLive }) => {
  if (isLive) {
    return (
      <span className="bg-red-50 text-red-600 text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5 border border-red-200 shadow-sm uppercase tracking-wider">
        <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span> Live Now
      </span>
    );
  }
  return (
    <span className="bg-stone-100 text-stone-500 text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 border border-stone-200 uppercase tracking-wider">
      <span className="w-1.5 h-1.5 bg-stone-400 rounded-full"></span> Offline
    </span>
  );
};

const LiveStreams = () => {
  const { role } = useAuth();
  const isAdmin = role === 'SUPER_ADMIN' || role === 'ADMIN';
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);

  // Multi-camera states
  const [selectedCentre, setSelectedCentre] = useState(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [cameras, setCameras] = useState([]);

  // Schedule states
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedCentreForSchedule, setSelectedCentreForSchedule] = useState(null);
  const [schedules, setSchedules] = useState([]);

  // Ad states
  const [isAdModalOpen, setIsAdModalOpen] = useState(false);
  const [selectedCentreForAd, setSelectedCentreForAd] = useState(null);
  const [adConfig, setAdConfig] = useState({ adVideoUrl: '', isAdSkippable: false });

  const openConfigModal = (centre) => {
    setSelectedCentre(centre);
    setCameras(centre.cameras || []);
    setIsConfigModalOpen(true);
  };

  const saveCameraConfig = async () => {
    try {
      const payload = { cameras };
      await api.put(`/centres/${selectedCentre._id}`, payload);
      
      setCentres(prev => prev.map(t => t._id === selectedCentre._id ? { ...t, cameras } : t));
      
      alert(`✅ Camera streams configured successfully for ${selectedCentre.name}!`);
      setIsConfigModalOpen(false);
    } catch (error) {
      console.error("Config save error:", error);
      alert("Failed to save camera settings: " + (error.response?.data?.message || error.message));
    }
  };

  const handleAddCamera = () => {
    setCameras([...cameras, { angleName: '', rtmpUrl: '' }]);
  };

  const handleCameraChange = (index, field, value) => {
    const updated = [...cameras];
    updated[index][field] = value;
    
    if (field === 'angleName' && !updated[index].rtmpUrl && value.trim() !== '') {
      const centreSlug = (selectedCentre?.name || 'centre').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 10);
      const angleSlug = value.toLowerCase().replace(/[^a-z0-9]/g, '');
      const randomId = Math.random().toString(16).substring(2, 10);
      updated[index].rtmpUrl = `rtmp://localhost:1935/live/sk_${centreSlug}_${angleSlug}_${randomId}`;
    }
    
    setCameras(updated);
  };

  const handleRemoveCamera = (index) => {
    setCameras(cameras.filter((_, i) => i !== index));
  };

  const openScheduleModal = (centre) => {
    setSelectedCentreForSchedule(centre);
    setSchedules(centre.schedule || []);
    setIsScheduleModalOpen(true);
  };

  const saveSchedule = async () => {
    try {
      await api.put(`/centres/${selectedCentreForSchedule._id}`, { schedule: schedules });
      setCentres(prev => prev.map(t => t._id === selectedCentreForSchedule._id ? { ...t, schedule: schedules } : t));
      alert(`✅ Schedule saved successfully for ${selectedCentreForSchedule.name}!`);
      setIsScheduleModalOpen(false);
    } catch (error) {
      console.error("Schedule save error:", error);
      alert("Failed to save schedule: " + (error.response?.data?.message || error.message));
    }
  };

  const openAdModal = (centre) => {
    setSelectedCentreForAd(centre);
    setAdConfig({
      adVideoUrl: centre.adVideoUrl || '',
      isAdSkippable: centre.isAdSkippable || false,
      ads: centre.ads || []
    });
    setIsAdModalOpen(true);
  };

  const saveAdConfig = async () => {
    try {
      await api.put(`/centres/${selectedCentreForAd._id}`, adConfig);
      setCentres(prev => prev.map(t => t._id === selectedCentreForAd._id ? { ...t, ...adConfig } : t));
      alert(`✅ Advertisement configuration saved successfully for ${selectedCentreForAd.name}!`);
      setIsAdModalOpen(false);
    } catch (error) {
      console.error("Ad config save error:", error);
      alert("Failed to save advertisement settings: " + (error.response?.data?.message || error.message));
    }
  };

  const handleAddAd = () => {
    setAdConfig(prev => ({
      ...prev,
      ads: [...(prev.ads || []), { videoUrl: '', isSkippable: false }]
    }));
  };

  const handleAdChange = (index, field, value) => {
    const updatedAds = [...(adConfig.ads || [])];
    updatedAds[index] = { ...updatedAds[index], [field]: value };
    setAdConfig(prev => ({
      ...prev,
      ads: updatedAds
    }));
  };

  const handleRemoveAd = (index) => {
    setAdConfig(prev => ({
      ...prev,
      ads: (prev.ads || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddScheduleRow = () => {
    setSchedules([...schedules, { eventName: '', day: 'Monday', time: '' }]);
  };

  const handleScheduleChange = (index, field, value) => {
    const updated = [...schedules];
    updated[index][field] = value;
    setSchedules(updated);
  };

  const handleRemoveScheduleRow = (index) => {
    setSchedules(schedules.filter((_, i) => i !== index));
  };

  useEffect(() => {
    fetchStreamData();
  }, []);

  const fetchStreamData = async () => {
    try {
      const data = (await api.get('/centres')).data;
      const results = data.data || data || [];
      setCentres(results);
      
    } catch (error) {
      console.error("Error fetching streams:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStreamStatus = async (id, currentStatus, name) => {
    try {
      await api.put(`/centres/${id}`, { isLive: !currentStatus });
      
      setCentres(prev => prev.map(t => t._id === id ? { ...t, isLive: !currentStatus } : t));
      alert(`✅ Stream ${currentStatus ? 'stopped' : 'started'} for ${name}`);
    } catch (error) {
      console.error("Toggle error:", error);
      alert("Failed to change stream status: " + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="font-serif text-3xl font-bold text-[#2A2858]">Live Streaming Control</h2>
          <p className="mt-1 text-stone-500 text-sm">Monitor and manage RTMP broadcast feeds</p>
        </div>
        <button className="bg-stone-800 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg hover:bg-stone-900 transition-colors flex items-center gap-2">
          <span className="material-symbols-outlined text-sm font-bold">refresh</span> Refresh Feeds
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-stone-500 font-bold animate-pulse">Loading Stream Servers...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {centres.map((centre) => {
            let streamKey = centre.streamKey;
            if (!streamKey && centre.rtmpUrl) {
              const parts = centre.rtmpUrl.split('/');
              const last = parts[parts.length - 1];
              if (last && last !== 'live') streamKey = last;
            }
            if (!streamKey) {
              streamKey = `live_${centre._id.substring(0, 8)}_secret`;
            }
            let rtmpBase = centre.rtmpUrl || `rtmp://${process.env.REACT_APP_RTMP_HOST || 'localhost'}:1935/live`;
            if (rtmpBase.endsWith(`/${streamKey}`)) {
              rtmpBase = rtmpBase.substring(0, rtmpBase.length - streamKey.length - 1);
            }
            const combinedLink = `${rtmpBase}/${streamKey}`;

            return (
              <div key={centre._id} className="bg-white rounded-2xl border border-[#E8B84B]/20 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden flex flex-col group">
                
                {/* Stream Player Placeholder / Viewer UI */}
                <div className="h-48 bg-black relative flex items-center justify-center overflow-hidden">
                  {centre.isLive ? (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 z-10" />
                      <img 
                        src={centre.coverImage || `https://images.unsplash.com/photo-1544923246-77307dd654ca?q=80&w=600`} 
                        alt="Stream Preview" 
                        className="absolute inset-0 w-full h-full object-cover opacity-60 animate-pulse"
                      />
                      <div className="z-20 text-center">
                        <span className="material-symbols-outlined text-6xl text-white drop-shadow-lg cursor-pointer hover:scale-110 transition-transform">play_circle</span>
                        <p className="text-white text-[10px] font-bold uppercase tracking-[0.2em] mt-2 drop-shadow-md">Monitoring Signal Active</p>
                      </div>
                      <div className="absolute bottom-4 left-4 z-20 flex items-center gap-3">
                         <div className="flex -space-x-2">
                           {[1,2,3].map(i => <div key={i} className="w-5 h-5 rounded-full border border-black bg-stone-200" />)}
                         </div>
                         {/* was `stream.viewerCount` — a never-built edit; the map variable is `centre` */}
                         <span className="text-white text-[10px] font-bold">{centre.viewerCount || 0} Viewers</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center">
                      <span className="material-symbols-outlined text-5xl text-stone-700 mb-2">videocam_off</span>
                      <p className="text-stone-500 text-[10px] font-bold tracking-widest uppercase">Standby Mode</p>
                    </div>
                  )}
                  <div className="absolute top-4 right-4 z-20">
                    <StatusBadge isLive={centre.isLive} />
                  </div>
                </div>
  
                {/* Controls Section */}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-serif text-xl font-bold text-stone-800 leading-tight">{centre.name}</h3>
                      <p className="text-[10px] text-stone-500 flex items-center gap-1 font-bold uppercase tracking-wider">
                        <span className="material-symbols-outlined text-[12px] text-[#E8B84B]">location_on</span> {centre.location}
                      </p>
                    </div>
                  </div>
  
                  <div className="bg-[#FAF9F6] rounded-xl p-4 mb-6 border border-[#E8B84B]/10 flex-1 space-y-4">
                    <div>
                      <label className="text-[10px] font-black text-[#2A2858] uppercase tracking-widest mb-2 block">Full RTMP URL (Combined)</label>
                      <div className="relative mb-4">
                        <input 
                          readOnly 
                          type="text" 
                          value={combinedLink}
                          className="w-full bg-white border border-stone-200 rounded-lg pl-3 pr-10 py-2.5 text-[11px] font-mono text-stone-600 focus:outline-none focus:border-[#E8B84B] shadow-inner font-bold"
                        />
                        <button 
                          onClick={() => {
                            navigator.clipboard.writeText(combinedLink);
                            alert('✅ Full RTMP URL copied!');
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#E8B84B] hover:text-[#2A2858] transition-colors"
                        >
                          <span className="material-symbols-outlined text-[18px]">content_copy</span>
                        </button>
                      </div>

                      <label className="text-[10px] font-black text-[#2A2858] uppercase tracking-widest mb-2 block">Main RTMP Configuration</label>
                      <div className="bg-white border border-stone-200 rounded-lg p-3">
                        <label className="text-[9px] font-bold text-stone-500 mb-1 block uppercase tracking-widest">Main Server URL</label>
                        <div className="relative mb-2">
                          <input 
                            readOnly 
                            type="text" 
                            value={rtmpBase}
                            className="w-full bg-stone-50 border border-stone-200 rounded-md pl-3 pr-10 py-2 text-[10px] font-mono text-stone-600 focus:outline-none"
                          />
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(rtmpBase);
                              alert('✅ Main Server URL copied!');
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-stone-400 hover:text-[#E8B84B]"
                          >
                            <span className="material-symbols-outlined text-[16px]">content_copy</span>
                          </button>
                        </div>

                        <label className="text-[9px] font-bold text-stone-500 mb-1 block uppercase tracking-widest">Main Stream Key</label>
                        <div className="relative">
                          <input 
                            readOnly 
                            type="text" 
                            value={streamKey}
                            className="w-full bg-stone-50 border border-stone-200 rounded-md pl-3 pr-10 py-2 text-[10px] font-mono text-stone-600 focus:outline-none"
                          />
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(streamKey);
                              alert('✅ Main Stream Key copied!');
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-stone-400 hover:text-[#E8B84B]"
                          >
                            <span className="material-symbols-outlined text-[16px]">content_copy</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-stone-200 border-dashed">
                      <label className="text-[10px] font-black text-[#2A2858] uppercase tracking-widest mt-2 mb-2 block">Additional Camera Details</label>
                      <div className="space-y-3">
                      {(!centre.cameras || centre.cameras.length === 0) ? (
                         <div className="text-[10px] text-stone-400 italic">No custom cameras configured.</div>
                      ) : (
                        centre.cameras.map((cam, idx) => {
                          let camStreamKey = '';
                          let camServerUrl = cam.rtmpUrl;
                          
                          if (cam.rtmpUrl) {
                            const parts = cam.rtmpUrl.split('/');
                            camStreamKey = parts[parts.length - 1];
                            if (cam.rtmpUrl.endsWith(`/${camStreamKey}`)) {
                              camServerUrl = cam.rtmpUrl.substring(0, cam.rtmpUrl.length - camStreamKey.length - 1);
                            }
                          }

                          return (
                            <div key={idx} className="bg-white border border-stone-200 rounded-lg p-3">
                              <h4 className="text-[10px] font-black text-stone-700 uppercase mb-2 border-b border-stone-100 pb-1">{cam.angleName}</h4>
                              
                              <label className="text-[9px] font-bold text-stone-500 mb-1 block uppercase tracking-widest">Server URL</label>
                              <div className="relative mb-2">
                                <input 
                                  readOnly 
                                  type="text" 
                                  value={camServerUrl}
                                  className="w-full bg-stone-50 border border-stone-200 rounded-md pl-3 pr-8 py-1.5 text-[10px] font-mono text-stone-600 focus:outline-none"
                                />
                                <button 
                                  onClick={() => {
                                    navigator.clipboard.writeText(camServerUrl);
                                    alert(`✅ ${cam.angleName} Server URL copied!`);
                                  }}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-[#E8B84B]"
                                >
                                  <span className="material-symbols-outlined text-[14px]">content_copy</span>
                                </button>
                              </div>

                              <label className="text-[9px] font-bold text-stone-500 mb-1 block uppercase tracking-widest">Stream Key</label>
                              <div className="relative">
                                <input 
                                  readOnly 
                                  type="text" 
                                  value={camStreamKey}
                                  className="w-full bg-stone-50 border border-stone-200 rounded-md pl-3 pr-8 py-1.5 text-[10px] font-mono text-stone-600 focus:outline-none"
                                />
                                <button 
                                  onClick={() => {
                                    navigator.clipboard.writeText(camStreamKey);
                                    alert(`✅ ${cam.angleName} Stream Key copied!`);
                                  }}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-[#E8B84B]"
                                >
                                  <span className="material-symbols-outlined text-[14px]">content_copy</span>
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    </div>

                    <p className="text-[9px] text-stone-400 mt-2 leading-relaxed">
                      Enter these RTMP URLs directly into your <span className="font-bold text-stone-600">OBS</span> settings.
                    </p>
                  </div>

                  {/* VR controls removed — the owner excluded VR/AR from this platform. */}
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-3">
                      <button 
                        onClick={() => toggleStreamStatus(centre._id, centre.isLive, centre.name)}
                        className={`flex-1 py-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 ${
                          centre.isLive 
                          ? "bg-white text-red-600 border-2 border-red-100 hover:bg-red-50" 
                          : "bg-[#2A2858] text-white hover:bg-[#14122B]"
                        }`}
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          {centre.isLive ? 'stop_circle' : 'sensors'}
                        </span>
                        {centre.isLive ? 'Stop Broadcast' : 'Start Live'}
                      </button>
                      <button 
                        onClick={() => openConfigModal(centre)}
                        className="p-3 bg-stone-100 hover:bg-[#E8B84B]/10 hover:text-[#E8B84B] text-stone-500 rounded-xl transition-all shadow-md"
                        title="Configure Camera Angles"
                      >
                        <span className="material-symbols-outlined text-[20px]">settings</span>
                      </button>
                      <button 
                        onClick={() => openScheduleModal(centre)}
                        className="p-3 bg-stone-100 hover:bg-[#E8B84B]/10 hover:text-[#E8B84B] text-stone-500 rounded-xl transition-all shadow-md"
                        title="Manage Schedule"
                      >
                        <span className="material-symbols-outlined text-[20px]">calendar_month</span>
                      </button>
                      <button 
                        onClick={() => openAdModal(centre)}
                        className="p-3 bg-stone-100 hover:bg-[#E8B84B]/10 hover:text-[#E8B84B] text-stone-500 rounded-xl transition-all shadow-md"
                        title="Manage Advertisements"
                      >
                        <span className="material-symbols-outlined text-[20px]">ad_units</span>
                      </button>
                    </div>

                    {isAdmin && (
                      <Link 
                        to={`/live-monitor/${centre._id}`}
                        className="w-full py-3 bg-[#FAF9F6] text-[#2A2858] border-2 border-[#2A2858]/10 rounded-xl font-bold text-xs flex items-center justify-center gap-2 hover:bg-[#2A2858]/5 transition-all shadow-sm active:scale-95"
                      >
                        <span className="material-symbols-outlined text-[18px]">forum</span>
                        Moderate Live Chat
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Camera Configuration Modal */}
      {isConfigModalOpen && selectedCentre && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-[#FAF9F6] border-b border-[#E8B84B]/20 px-6 py-4 flex justify-between items-center shrink-0">
              <h3 className="font-serif text-lg font-bold text-[#2A2858] flex items-center gap-2">
                <span className="material-symbols-outlined">settings</span> Configure Camera Feeds
              </h3>
              <button 
                onClick={() => setIsConfigModalOpen(false)} 
                className="text-stone-400 hover:text-red-500 transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs text-stone-500 italic">
                  Define the camera feeds and RTMP streams for <strong className="text-stone-700">{selectedCentre.name}</strong>.
                </p>
                <button 
                  onClick={handleAddCamera}
                  className="bg-stone-50 text-[#2A2858] px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-[#E8B84B]/30 hover:bg-[#E8B84B]/10 transition-colors flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-[14px]">add</span> Add Camera
                </button>
              </div>

              <div className="space-y-3">
                {cameras.length === 0 ? (
                  <div className="text-center py-6 bg-stone-50 border border-stone-200 rounded-lg border-dashed">
                    <p className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">No extra cameras added</p>
                  </div>
                ) : (
                  cameras.map((camera, index) => (
                    <div key={index} className="flex flex-col gap-2 bg-stone-50 p-3 border border-stone-200 rounded-lg relative group">
                      <button 
                        type="button" 
                        onClick={() => handleRemoveCamera(index)}
                        className="absolute top-2 right-2 text-stone-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <span className="material-symbols-outlined text-[16px]">close</span>
                      </button>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-6">
                        <div>
                          <label className="block text-[9px] font-bold text-stone-500 uppercase mb-1">Angle Name</label>
                          <input
                            type="text"
                            value={camera.angleName}
                            onChange={(e) => handleCameraChange(index, 'angleName', e.target.value)}
                            list={`camera-suggestions-${index}`}
                            placeholder="e.g. Front View"
                            className="w-full border border-stone-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-[#E8B84B]"
                          />
                          <datalist id={`camera-suggestions-${index}`}>
                            <option value="Front View" />
                            <option value="Side View" />
                            <option value="Altar Cam" />
                            <option value="Top View" />
                            <option value="Wide Angle" />
                            <option value="Priest Cam" />
                            <option value="Main Gate" />
                            <option value="Drone View" />
                          </datalist>
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-stone-500 uppercase mb-1">RTMP URL</label>
                          <input
                            type="text"
                            value={camera.rtmpUrl}
                            onChange={(e) => handleCameraChange(index, 'rtmpUrl', e.target.value)}
                            placeholder="rtmp://..."
                            className="w-full border border-stone-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-[#E8B84B]"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="pt-4 border-t border-stone-100 flex gap-3">
                <button 
                  type="button" 
                  onClick={() => setIsConfigModalOpen(false)} 
                  className="w-1/2 bg-stone-100 text-stone-500 font-bold py-2.5 rounded-xl hover:bg-stone-200 transition-colors text-xs"
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={saveCameraConfig} 
                  className="w-1/2 bg-[#2A2858] text-white font-bold py-2.5 rounded-xl hover:bg-[#14122B] transition-colors text-xs shadow-lg"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Configuration Modal */}
      {isScheduleModalOpen && selectedCentreForSchedule && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-[#FAF9F6] border-b border-[#E8B84B]/20 px-6 py-4 flex justify-between items-center shrink-0">
              <h3 className="font-serif text-lg font-bold text-[#2A2858] flex items-center gap-2">
                <span className="material-symbols-outlined">calendar_month</span> Manage Stream Schedule
              </h3>
              <button 
                onClick={() => setIsScheduleModalOpen(false)} 
                className="text-stone-400 hover:text-red-500 transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs text-stone-500 italic">
                  Define the live stream schedule for <strong className="text-stone-700">{selectedCentreForSchedule.name}</strong>.
                </p>
                <button 
                  onClick={handleAddScheduleRow}
                  className="bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-green-200 hover:bg-green-100 transition-colors flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-[14px]">add</span> Add Event
                </button>
              </div>
              
              {schedules.length === 0 ? (
                <div className="text-center py-8 bg-stone-50 rounded-xl border border-stone-200 border-dashed">
                  <span className="material-symbols-outlined text-4xl text-stone-300 mb-2">event_busy</span>
                  <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">No schedule defined</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {schedules.map((item, idx) => (
                    <div key={idx} className="flex gap-3 items-start bg-stone-50 p-3 rounded-xl border border-stone-200">
                      <div className="flex-1">
                        <label className="block text-[9px] font-bold text-stone-500 uppercase tracking-widest mb-1">Event Name</label>
                        <input 
                          type="text" 
                          value={item.eventName} 
                          onChange={e => handleScheduleChange(idx, 'eventName', e.target.value)}
                          placeholder="e.g. Morning Aarti"
                          className="w-full border border-stone-200 rounded-lg px-3 py-2 text-xs font-medium text-stone-700 focus:outline-none focus:border-[#E8B84B]"
                        />
                      </div>
                      <div className="w-32">
                        <label className="block text-[9px] font-bold text-stone-500 uppercase tracking-widest mb-1">Day</label>
                        <select
                          value={item.day}
                          onChange={e => handleScheduleChange(idx, 'day', e.target.value)}
                          className="w-full border border-stone-200 rounded-lg px-3 py-2 text-xs font-medium text-stone-700 focus:outline-none focus:border-[#E8B84B]"
                        >
                          {['Everyday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>
                      <div className="w-32">
                        <label className="block text-[9px] font-bold text-stone-500 uppercase tracking-widest mb-1">Time</label>
                        <input 
                          type="text" 
                          value={item.time} 
                          onChange={e => handleScheduleChange(idx, 'time', e.target.value)}
                          placeholder="07:00 PM"
                          className="w-full border border-stone-200 rounded-lg px-3 py-2 text-xs font-medium text-stone-700 focus:outline-none focus:border-[#E8B84B]"
                        />
                      </div>
                      <button 
                        onClick={() => handleRemoveScheduleRow(idx)}
                        className="mt-5 p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove Event"
                      >
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-4 border-t border-stone-100 flex gap-3 mt-4">
                <button 
                  type="button" 
                  onClick={() => setIsScheduleModalOpen(false)} 
                  className="w-1/2 bg-stone-100 text-stone-500 font-bold py-2.5 rounded-xl hover:bg-stone-200 transition-colors text-xs"
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={saveSchedule} 
                  className="w-1/2 bg-[#2A2858] text-white font-bold py-2.5 rounded-xl hover:bg-[#14122B] transition-colors text-xs shadow-lg"
                >
                  Save Schedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Advertisement Modal */}
      {isAdModalOpen && selectedCentreForAd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-[#FAF9F6] border-b border-[#E8B84B]/20 px-6 py-4 flex justify-between items-center shrink-0">
              <h3 className="font-serif text-lg font-bold text-[#2A2858] flex items-center gap-2">
                <span className="material-symbols-outlined">ad_units</span> Manage Advertisements
              </h3>
              <button 
                onClick={() => setIsAdModalOpen(false)} 
                className="text-stone-400 hover:text-red-500 transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
              <p className="text-xs text-stone-500 italic">
                Manage the live stream ads for <strong className="text-stone-700">{selectedCentreForAd.name}</strong>. The system will randomly pick and play one of the videos below during the stream.
              </p>
              
              <div className="flex justify-between items-center border-b border-stone-100 pb-2">
                <h4 className="text-xs font-black text-[#2A2858] uppercase tracking-widest">Ad Video List</h4>
                <button 
                  type="button"
                  onClick={handleAddAd}
                  className="bg-stone-50 text-[#2A2858] px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-[#E8B84B]/30 hover:bg-[#E8B84B]/10 transition-colors flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-[14px]">add</span> Add Ad Video
                </button>
              </div>

              {(!adConfig.ads || adConfig.ads.length === 0) ? (
                <div className="text-center py-6 bg-stone-50 border border-stone-200 rounded-xl border-dashed">
                  <p className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">No ad videos configured</p>
                  <p className="text-[9px] text-stone-400 mt-1">Please add at least one ad video.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[250px] overflow-y-auto pr-1">
                  {adConfig.ads.map((ad, index) => (
                    <div key={index} className="bg-stone-50 p-3 border border-stone-200 rounded-xl relative group">
                      <button 
                        type="button" 
                        onClick={() => handleRemoveAd(index)}
                        className="absolute top-2 right-2 text-stone-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove Ad"
                      >
                        <span className="material-symbols-outlined text-[16px]">close</span>
                      </button>

                      <div className="space-y-2 pr-6">
                        <div>
                          <label className="block text-[9px] font-bold text-stone-500 uppercase mb-1">Video URL ({index + 1})</label>
                          <input
                            type="text"
                            value={ad.videoUrl}
                            onChange={(e) => handleAdChange(index, 'videoUrl', e.target.value)}
                            placeholder="e.g. MP4 link or YouTube URL"
                            className="w-full border border-stone-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-[#E8B84B]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            id={`isSkippable-${index}`} 
                            checked={ad.isSkippable}
                            onChange={(e) => handleAdChange(index, 'isSkippable', e.target.checked)}
                            className="w-3.5 h-3.5 text-[#2A2858] border-stone-300 rounded focus:ring-[#2A2858] cursor-pointer" 
                          />
                          <label htmlFor={`isSkippable-${index}`} className="text-[10px] font-bold text-stone-600 cursor-pointer">
                            Allow users to skip this ad
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-2 border-t border-stone-100 mt-4">
                <label className="block text-[10px] font-bold text-[#2A2858] uppercase tracking-widest mb-1">Legacy/Fallback Ad Video URL</label>
                <input 
                  type="text" 
                  value={adConfig.adVideoUrl} 
                  onChange={e => setAdConfig({...adConfig, adVideoUrl: e.target.value})}
                  placeholder="Only used if the above list is empty"
                  className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-xs font-medium text-stone-700 focus:outline-none focus:border-[#E8B84B]"
                />
              </div>

              <div className="flex gap-3 mt-4">
                <button 
                  type="button" 
                  onClick={() => setIsAdModalOpen(false)} 
                  className="w-1/2 bg-stone-100 text-stone-500 font-bold py-2.5 rounded-xl hover:bg-stone-200 transition-colors text-xs"
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={saveAdConfig} 
                  className="w-1/2 bg-[#2A2858] text-white font-bold py-2.5 rounded-xl hover:bg-[#14122B] transition-colors text-xs shadow-lg"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveStreams;